/**
 * CloudMap Utilities
 * Helper functions for distance, URL state, etc.
 */

/**
 * Calculate distance between two points using Haversine formula
 * @returns distance in kilometers
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Format distance for display
 */
export function formatDistance(km) {
    if (km < 1) {
        return `${Math.round(km * 1000)} m`;
    } else if (km < 100) {
        return `${Math.round(km)} km`;
    } else {
        return `${Math.round(km).toLocaleString()} km`;
    }
}

/**
 * Wait for Google Maps API to be ready
 */
export function waitForGoogleMaps() {
    return new Promise((resolve, reject) => {
        if (window.google?.maps) {
            resolve();
            return;
        }

        let attempts = 0;
        const maxAttempts = 50;

        const check = () => {
            attempts++;
            if (window.google?.maps) {
                resolve();
            } else if (attempts >= maxAttempts) {
                reject(new Error('Google Maps failed to load'));
            } else {
                setTimeout(check, 100);
            }
        };

        check();
    });
}

/**
 * Parse URL state for initial config
 */
export function parseUrlState(defaults) {
    const params = new URLSearchParams(window.location.search);
    const state = {
        activeProviders: new Set(),
        center: defaults.center,
        zoom: defaults.zoom,
    };

    // Providers
    const providers = params.get('p');
    if (providers) {
        providers.split(',').forEach(p => {
            state.activeProviders.add(p.trim());
        });
    }

    // Map position
    const lat = parseFloat(params.get('lat'));
    const lng = parseFloat(params.get('lng'));
    const zoom = parseFloat(params.get('z'));

    if (!isNaN(lat) && !isNaN(lng)) {
        state.center = {lat, lng};
    }

    if (!isNaN(zoom)) {
        state.zoom = zoom;
    }

    return state;
}

/**
 * Update URL with current state (preserves existing query params)
 */
export function updateUrl(map, activeProviders, totalProviders) {
    // Start with existing params to preserve them
    const params = new URLSearchParams(window.location.search);

    // Cloudmap-specific params
    const cloudmapParams = ['p', 'lat', 'lng', 'z'];

    // Providers (only if not all selected)
    if (activeProviders.size < totalProviders) {
        params.set('p', Array.from(activeProviders).join(','));
    } else {
        params.delete('p');
    }

    // Map position
    if (map) {
        const center = map.getCenter();
        const zoom = map.getZoom();

        params.set('lat', center.lat().toFixed(4));
        params.set('lng', center.lng().toFixed(4));
        params.set('z', zoom.toFixed(1));
    }

    // Update URL without reload
    const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

    window.history.replaceState({}, '', newUrl);
}
